<script setup lang="ts">
import IndexLayout from "~/components/Indexs/IndexLayout.vue";
import IndexLayoutSkeleton from "~/components/Skeleton/IndexLayoutSkeleton.vue";
import { usePagination } from "~/composables/paginations/usePagination";
import type { IIndexHeader, IPaginationApi } from "~/utils/interfaces/table-interfaces";
import FooterActions from "~/components/Common/FooterActions.vue";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";
import { ContextFiltersEnum } from "~/utils/enums/context-filters.enums";
import { useLearningPathTrainingsAttendees } from "~/composables/learning-path/useLearningPathTrainingsAttendees";
import TrainingsAttendeesView from "./view/TrainingsAttendeesView.vue";
import { useLearningPathTrainingsStore } from "~/store/learning-path/learning-path-trainings.store";
import { useLearningPathAttendees } from "~/composables/learning-path/useLearningPathAttendees";
import { LearningPathAttendeesTabTypeEnum } from "~/utils/enums/learning-path.enums";
import { useLearningPathAttendeesStore } from "~/store/learning-path/learning-path-attendees.store";
import EmptyState from "~/components/Common/EmptyState.vue";
import { GridStyle } from "~/utils/enums/common.enums";

const props = defineProps<{
  learningPathTrainingId: string;
  isLoading: boolean;
  isError: boolean;
}>();
const maxHeight = ref<string>("calc(100vh - 300px)");
const { headers, getNav, footersActionsTrainingsAttendees } = useLearningPathTrainingsAttendees();
const { setCurrentNav } = useLearningPathAttendeesStore();
const paginate = usePagination(PaginationKeysEnum.LEARNING_PATH_TRAININGS_ATTENDEES_PAGINATION);
const {
  learningPathTrainingsAttendeesFullDatas,
  learningPathTrainingsAttendeesMultiSelect,
  learningPathTrainingsAttendeesSelectAll,
} = storeToRefs(useLearningPathTrainingsStore());
const { fetchLearningPathTrainingsAttendeesFullDatas } = useLearningPathTrainingsStore();
const searchMessage: Ref<string> = ref<string>("");
const showActionFooter = ref<boolean>(false);
const visibleHeaders = computed(() => headers.value.filter((h: IIndexHeader) => h.visible));
paginate.setFetchFunction(async () => {
  const transformQuery = paginate.getFindOptions(paginate.paginateOptions.value);
  await fetchLearningPathTrainingsAttendeesFullDatas(
    props.learningPathTrainingId,
    transformQuery,
    ContextFiltersEnum.LEARNING_PATH_TRAININGS_ATTENDEES_CONTEXT,
  );
});

watch(
  () => learningPathTrainingsAttendeesMultiSelect.value?.selectedItems,
  () => {
    if (learningPathTrainingsAttendeesMultiSelect.value?.selectedItems?.length) {
      showActionFooter.value = true;
    } else if (
      !learningPathTrainingsAttendeesMultiSelect.value?.selectedItems?.length &&
      !learningPathTrainingsAttendeesSelectAll.value
    ) {
      showActionFooter.value = false;
    }
  },
);

function handleAddAttendees() {
  const { findLearningPathNav } = useLearningPathAttendees();
  const nav = findLearningPathNav(LearningPathAttendeesTabTypeEnum.ATTENDEES);
  if (nav) {
    setCurrentNav(nav);
  }
}
</script>

<template>
  <div v-if="props.isLoading">
    <IndexLayoutSkeleton :filters="1" :actions="2" :lane="9" add pagination />
  </div>
  <IndexLayout
    v-else
    ref="learningPathTrainingsAttendeesMultiSelect"
    :grid-style="GridStyle.SECONDARY"
    :empty="!props.isLoading && !props.isError && !learningPathTrainingsAttendeesFullDatas?.data?.length"
    :rows="learningPathTrainingsAttendeesFullDatas as IPaginationApi<any>"
    track-by="userId"
    :headers="visibleHeaders"
    :nav="getNav()"
    :checkbox="true"
    :select-all="learningPathTrainingsAttendeesSelectAll"
    :item-per-page="paginate.paginateOptions.value.limit as number"
    :search-message="searchMessage as string"
    url="/users?userId=__KEY__"
    :item-keys="['userId']"
    :resize="true"
    :pagination-key="PaginationKeysEnum.LEARNING_PATH_TRAININGS_ATTENDEES_PAGINATION"
    :page-container="true"
    :max-height="maxHeight"
    @page-navigate="paginate.pageNavigateWithoutSettings($event)"
    @tab-navigate="paginate.tabNavigateWithoutSettings($event)"
    @sort="paginate.sortColumnWithoutSettings($event)"
  >
    <template #row="{ row }">
      <TrainingsAttendeesView :row="row"></TrainingsAttendeesView>
    </template>
    <template #empty>
      <EmptyState message="Tous les participants sont bien inscrits !" icon="target-menu" size="medium"> </EmptyState>
    </template>
  </IndexLayout>

  <div class="footer z-[104] pointer-events-none">
    <FooterActions
      v-if="showActionFooter"
      v-model="learningPathTrainingsAttendeesSelectAll"
      :footer-actions="footersActionsTrainingsAttendees"
      :items-name="`Participants`"
      :excluded-items-count="learningPathTrainingsAttendeesMultiSelect?.excludedItems?.length || 0"
      :selected-items-count="learningPathTrainingsAttendeesMultiSelect?.selectedItems?.length || 0"
      :total-count="learningPathTrainingsAttendeesFullDatas?.meta?.count || 0"
    ></FooterActions>
  </div>
</template>

<style lang="scss" scoped>
.footer {
  bottom: 50px;
  position: fixed;
  width: 100%;
  left: 50%;
  transform: translateX(-50%);
}
</style>
